import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  FileText, Upload, Trash2, Edit2, Plus, X, Check,
  ExternalLink, GripVertical, Download
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const SA_HEADERS = () => ({ Authorization: `Bearer ${localStorage.getItem("sa_token")}` });

const COLORI = [
  { key: "indigo",  bg: "bg-indigo-600",  light: "bg-indigo-50",  text: "text-indigo-600",  border: "border-indigo-200",  label: "Indigo" },
  { key: "emerald", bg: "bg-emerald-600", light: "bg-emerald-50", text: "text-emerald-600", border: "border-emerald-200", label: "Verde" },
  { key: "rose",    bg: "bg-rose-500",    light: "bg-rose-50",    text: "text-rose-600",    border: "border-rose-200",    label: "Rosa" },
  { key: "orange",  bg: "bg-orange-500",  light: "bg-orange-50",  text: "text-orange-600",  border: "border-orange-200",  label: "Arancio" },
  { key: "purple",  bg: "bg-purple-600",  light: "bg-purple-50",  text: "text-purple-600",  border: "border-purple-200",  label: "Viola" },
  { key: "amber",   bg: "bg-amber-500",   light: "bg-amber-50",   text: "text-amber-600",   border: "border-amber-200",   label: "Ambra" },
];

const getColore = (key) => COLORI.find(c => c.key === key) || COLORI[0];

export default function MaterialiVenditaPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/superadmin/materiali-vendita`, { headers: SA_HEADERS() });
      setItems(res.data);
    } catch { toast.error("Errore caricamento"); }
    setLoading(false);
  };

  useEffect(() => { fetch(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questo materiale?")) return;
    try {
      await axios.delete(`${API}/api/superadmin/materiali-vendita/${id}`, { headers: SA_HEADERS() });
      toast.success("Eliminato");
      fetch();
    } catch { toast.error("Errore"); }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6" data-testid="materiali-vendita-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Materiali Vendita</h2>
          <p className="text-sm text-gray-500 mt-0.5">PDF condivisi con tutti i venditori</p>
        </div>
        <Button
          onClick={() => { setEditItem(null); setShowForm(true); }}
          className="bg-indigo-600 hover:bg-indigo-700 rounded-xl gap-2"
          data-testid="add-materiale-btn"
        >
          <Plus className="h-4 w-4" /> Aggiungi
        </Button>
      </div>

      {/* Form aggiunta/modifica */}
      {showForm && (
        <MaterialeForm
          item={editItem}
          onSave={() => { setShowForm(false); setEditItem(null); fetch(); }}
          onCancel={() => { setShowForm(false); setEditItem(null); }}
        />
      )}

      {/* Lista */}
      {loading ? (
        <p className="text-sm text-gray-500 text-center py-10">Caricamento...</p>
      ) : items.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-2xl">
          <FileText className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-500">Nessun materiale ancora caricato</p>
          <p className="text-xs text-gray-400 mt-1">Clicca "Aggiungi" per caricare il primo PDF</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(item => {
            const colore = getColore(item.colore);
            const fileUrl = `${API}/api/superadmin/files/${item.file_path}`;
            return (
              <div
                key={item.id}
                className={`flex items-center gap-4 p-4 rounded-2xl border ${colore.border} ${colore.light}`}
                data-testid={`materiale-${item.id}`}
              >
                <div className={`w-11 h-11 rounded-xl ${colore.bg} flex items-center justify-center flex-shrink-0`}>
                  <FileText className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-gray-900 truncate">{item.titolo}</p>
                  {item.descrizione && <p className="text-xs text-gray-500 truncate mt-0.5">{item.descrizione}</p>}
                  <p className="text-[10px] text-gray-400 mt-1">{item.file_nome}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <a href={fileUrl} target="_blank" rel="noreferrer">
                    <Button size="sm" variant="outline" className="h-8 rounded-lg text-xs gap-1 border-gray-300">
                      <ExternalLink className="h-3 w-3" /> Apri
                    </Button>
                  </a>
                  <Button
                    size="sm" variant="ghost" className="h-8 w-8 rounded-lg text-gray-400 hover:text-indigo-600"
                    onClick={() => { setEditItem(item); setShowForm(true); }}
                    data-testid={`edit-materiale-${item.id}`}
                  >
                    <Edit2 className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm" variant="ghost" className="h-8 w-8 rounded-lg text-gray-400 hover:text-red-600"
                    onClick={() => handleDelete(item.id)}
                    data-testid={`delete-materiale-${item.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MaterialeForm({ item, onSave, onCancel }) {
  const [titolo, setTitolo] = useState(item?.titolo || "");
  const [descrizione, setDescrizione] = useState(item?.descrizione || "");
  const [colore, setColore] = useState(item?.colore || "indigo");
  const [ordine, setOrdine] = useState(item?.ordine ?? 0);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const handleSave = async () => {
    if (!titolo.trim()) { toast.error("Inserisci un titolo"); return; }
    if (!item && !file) { toast.error("Seleziona un file PDF"); return; }
    setSaving(true);
    try {
      if (item) {
        // Solo aggiornamento metadati (senza nuovo file)
        await axios.put(
          `${API}/api/superadmin/materiali-vendita/${item.id}`,
          { titolo, descrizione, colore, ordine },
          { headers: SA_HEADERS() }
        );
      } else {
        const fd = new FormData();
        fd.append("titolo", titolo);
        fd.append("descrizione", descrizione);
        fd.append("colore", colore);
        fd.append("ordine", ordine);
        fd.append("file", file);
        await axios.post(`${API}/api/superadmin/materiali-vendita`, fd, {
          headers: { ...SA_HEADERS(), "Content-Type": "multipart/form-data" }
        });
      }
      toast.success(item ? "Aggiornato" : "Caricato con successo");
      onSave();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
    setSaving(false);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4 shadow-sm" data-testid="materiale-form">
      <div className="flex items-center justify-between">
        <p className="font-bold text-sm text-gray-900">{item ? "Modifica materiale" : "Nuovo materiale"}</p>
        <button onClick={onCancel} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
      </div>

      <div className="space-y-3">
        <Input
          value={titolo} onChange={e => setTitolo(e.target.value)}
          placeholder="Titolo (es. Presentazione per i Clienti)"
          data-testid="input-titolo-materiale"
        />
        <Input
          value={descrizione} onChange={e => setDescrizione(e.target.value)}
          placeholder="Descrizione breve (opzionale)"
          data-testid="input-desc-materiale"
        />
        <Input
          type="number" value={ordine} onChange={e => setOrdine(Number(e.target.value))}
          placeholder="Ordine (0 = primo)"
          className="w-32"
        />

        {/* Selezione colore */}
        <div>
          <p className="text-xs font-semibold text-gray-500 mb-2">Colore card</p>
          <div className="flex gap-2 flex-wrap">
            {COLORI.map(c => (
              <button
                key={c.key}
                onClick={() => setColore(c.key)}
                className={`w-8 h-8 rounded-full ${c.bg} transition-all ${colore === c.key ? "ring-2 ring-offset-2 ring-gray-900 scale-110" : "opacity-70 hover:opacity-100"}`}
                title={c.label}
              />
            ))}
          </div>
        </div>

        {/* Upload file */}
        {!item && (
          <div>
            <input
              ref={fileRef} type="file" accept=".pdf,.doc,.docx,.pptx,.xlsx"
              className="hidden"
              onChange={e => setFile(e.target.files[0] || null)}
              data-testid="input-file-materiale"
            />
            <button
              onClick={() => fileRef.current?.click()}
              className="w-full border-2 border-dashed border-gray-200 rounded-xl p-4 flex items-center justify-center gap-2 text-sm text-gray-500 hover:border-indigo-300 hover:text-indigo-600 transition-colors"
            >
              {file ? (
                <><Check className="h-4 w-4 text-emerald-500" /><span className="text-emerald-600 font-medium">{file.name}</span></>
              ) : (
                <><Upload className="h-4 w-4" />Seleziona PDF o documento</>
              )}
            </button>
          </div>
        )}
      </div>

      <div className="flex gap-3 pt-1">
        <Button onClick={handleSave} disabled={saving} className="flex-1 bg-indigo-600 hover:bg-indigo-700 rounded-xl" data-testid="save-materiale-btn">
          {saving ? "Salvataggio..." : item ? "Salva modifiche" : "Carica materiale"}
        </Button>
        <Button variant="outline" onClick={onCancel} className="rounded-xl">Annulla</Button>
      </div>
    </div>
  );
}
